/**
 * BikeComparisonSelectorDrawer — right-side drawer to pick Workout B for comparison.
 *
 * Tabs: Upload | My Files | Community
 * On confirm calls onStart({ type, ... })
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Drawer, Box, Typography, Tabs, Tab, Button,
  CircularProgress, useTheme, useMediaQuery, TextField, IconButton,
} from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { tokens } from '../theme';
import { getMyBikeFitFiles, listCommunityBikeWorkouts } from '../api/bikeComparisonApi';

function fmtDist(km) { return km == null ? '' : `${Number(km).toFixed(1)} km`; }
function fmtDur(s) {
  if (s == null) return '';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}m`;
}

const MY_FILES_COLUMNS = [
  { field: 'filename', headerName: 'File', flex: 2, minWidth: 150 },
  { field: 'total_distance', headerName: 'Dist', width: 90, valueFormatter: v => fmtDist(v ? v / 1000 : null) },
  {
    field: 'workout_date', headerName: 'Date', width: 110,
    valueFormatter: v => v ? new Date(v).toLocaleDateString() : '',
  },
];

const COMMUNITY_COLUMNS = [
  { field: 'filename', headerName: 'File', flex: 2, minWidth: 150 },
  { field: 'total_distance_km', headerName: 'Dist', width: 90, valueFormatter: v => fmtDist(v) },
  { field: 'finish_time_seconds', headerName: 'Duration', width: 100, valueFormatter: v => fmtDur(v) },
  {
    field: 'shared_at', headerName: 'Date', width: 110,
    valueFormatter: v => v ? new Date(v).toLocaleDateString() : '',
  },
];

function SearchToolbar() {
  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <GridToolbarQuickFilter debounceMs={200} fullWidth size="small" />
    </Box>
  );
}

export default function BikeComparisonSelectorDrawer({ open, onClose, onStart }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [source, setSource] = useState(null);

  // Upload
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // My Files
  const [myFiles, setMyFiles] = useState([]);
  const [myFilesLoading, setMyFilesLoading] = useState(false);
  const [myFilesLoaded, setMyFilesLoaded] = useState(false);

  // Community
  const [communityFiles, setCommunityFiles] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityLoaded, setCommunityLoaded] = useState(false);

  // Per-tab selected row id
  const [selectionMap, setSelectionMap] = useState({});

  const loadMyFiles = useCallback(async () => {
    if (myFilesLoaded) return;
    setMyFilesLoading(true);
    try {
      const data = await getMyBikeFitFiles();
      setMyFiles(data || []);
      setMyFilesLoaded(true);
    } catch { /* ignore */ } finally {
      setMyFilesLoading(false);
    }
  }, [myFilesLoaded]);

  const loadCommunity = useCallback(async () => {
    if (communityLoaded) return;
    setCommunityLoading(true);
    try {
      const data = await listCommunityBikeWorkouts({ page_size: 200 });
      setCommunityFiles(data?.workouts || []);
      setCommunityLoaded(true);
    } catch { /* ignore */ } finally {
      setCommunityLoading(false);
    }
  }, [communityLoaded]);

  useEffect(() => {
    if (!open) {
      setUploadFile(null);
      setSource(null);
      setSelectionMap({});
      setTab(0);
      setDragOver(false);
      return;
    }
    if (tab === 1 && !myFilesLoaded) loadMyFiles();
    if (tab === 2 && !communityLoaded) loadCommunity();
  }, [open, tab, myFilesLoaded, communityLoaded, loadMyFiles, loadCommunity]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) { setUploadFile(f); setSource({ type: 'upload', file: f }); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) { setUploadFile(f); setSource({ type: 'upload', file: f }); }
  };

  const handleStart = () => {
    if (source) onStart(source);
  };

  const canConfirm = !!source;

  const gridSx = {
    height: '100%',
    border: 'none',
    fontSize: '0.8rem',
    '& .MuiDataGrid-row': { cursor: 'pointer' },
    '& .MuiDataGrid-row.picker-row-selected': {
      backgroundColor: `${colors.blueAccent[900]} !important`,
    },
    '& .MuiDataGrid-row.picker-row-selected:hover': {
      backgroundColor: `${colors.blueAccent[800]} !important`,
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
  };

  const renderGrid = (rows, columns, tabKey, loading) => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    if (rows.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
          <Typography color={colors.grey[400]}>No items found</Typography>
        </Box>
      );
    }
    const sel = selectionMap[tabKey] ?? null;
    return (
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        getRowClassName={(p) => p.row.id === sel ? 'picker-row-selected' : ''}
        onRowClick={({ row }) => {
          setSelectionMap(prev => ({ ...prev, [tabKey]: row.id }));
          if (tabKey === 'myFiles') setSource({ type: 'myfile', fileId: row.id, record: row });
          else setSource({ type: 'community', sharedId: row.id, record: row });
        }}
        slots={{ toolbar: SearchToolbar }}
        disableColumnMenu
        disableRowSelectionOnClick
        initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
        pageSizeOptions={[25, 50, 100]}
        sx={gridSx}
      />
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : '80%',
          backgroundColor: colors.grey[800],
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex" alignItems="center" justifyContent="space-between"
        px={2} py={1.5}
        sx={{ flexShrink: 0, borderBottom: `1px solid ${colors.grey[700]}` }}
      >
        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
          Select Workout B
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.grey[300] }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setSource(null); setUploadFile(null); setDragOver(false); }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexShrink: 0,
          borderBottom: `1px solid ${colors.grey[700]}`,
          '& .MuiTab-root': { color: colors.grey[300] },
          '& .Mui-selected': { color: colors.blueAccent[400] },
          '& .MuiTabs-indicator': { backgroundColor: colors.blueAccent[400] },
        }}
      >
        <Tab label="Upload" />
        <Tab label="My Files" />
        <Tab label="Community" />
      </Tabs>

      {/* Content */}
      <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>

        {/* Upload tab */}
        {tab === 0 && (
          <Box p={2} flex={1} overflow="auto">
            <Box
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragOver ? colors.blueAccent[400] : colors.grey[600]}`,
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: dragOver ? colors.grey[700] : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 40, color: colors.blueAccent[400], mb: 1 }} />
              <Typography variant="body1" color={colors.grey[200]}>
                {uploadFile ? uploadFile.name : 'Drop a .fit file here or click to browse'}
              </Typography>
              {uploadFile && (
                <Typography variant="caption" color={colors.grey[400]}>
                  {(uploadFile.size / 1024).toFixed(0)} KB
                </Typography>
              )}
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept=".fit"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </Box>
        )}

        {/* My Files tab */}
        {tab === 1 && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(myFiles, MY_FILES_COLUMNS, 'myFiles', myFilesLoading)}
          </Box>
        )}

        {/* Community tab */}
        {tab === 2 && (
          <Box display="flex" flexDirection="column" flex={1} sx={{ minHeight: 0 }}>
            {renderGrid(communityFiles, COMMUNITY_COLUMNS, 'community', communityLoading)}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ flexShrink: 0, borderTop: `1px solid ${colors.grey[700]}`, p: 2 }}>
        <Box display="flex" gap={1.5}>
          <Button
            variant="outlined"
            onClick={onClose}
            fullWidth
            sx={{
              color: colors.grey[300],
              borderColor: colors.grey[700],
              '&:hover': { borderColor: colors.grey[300] },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!canConfirm}
            onClick={handleStart}
            fullWidth
            sx={{
              backgroundColor: colors.blueAccent[600],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.blueAccent[500] },
              '&:disabled': { backgroundColor: colors.grey[700], color: colors.grey[500] },
            }}
          >
            Compare
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
